import { useEffect, useState } from 'react';
import { addMethod, updateMethod } from '../../lib/labData';
import { parseSteps, stepsToText, TEMP_UNITS } from '../../lib/instructions';

const LABEL_SUGGESTIONS = [
  'Melt oils to',
  'Heat water phase to',
  'Emulsify at',
  'Add fragrance at',
  'Add preservative below',
  'Pour at',
  'Trace at',
];

const blankTemp = () => ({ label: '', low: '', high: '', unit: 'C' });

const toNumber = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// A method is a shared object: Orchard Bloom, Warm Vanilla and Paradise Silk
// are all made the same way, so the steps are written once and attached to
// each. Saving one never creates a formula version.
export default function MethodForm({ method, productTypes, onClose, onSaved }) {
  const editing = Boolean(method?.id);
  const [name, setName] = useState(method?.name || '');
  const [productTypeId, setProductTypeId] = useState(method?.product_type_id || '');
  const [steps, setSteps] = useState(stepsToText(method?.steps || []));
  const [temps, setTemps] = useState(() => {
    const rows = Array.isArray(method?.temperatures) ? method.temperatures : [];
    return rows.length
      ? rows.map((t) => ({
          label: t.label || '',
          low: t.low ?? '',
          high: t.high ?? '',
          unit: TEMP_UNITS.includes(t.unit) ? t.unit : 'C',
        }))
      : [blankTemp()];
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const updateTemp = (i, field, value) =>
    setTemps((rows) => rows.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Give the method a name — you attach it to formulas by that name.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Blank rows are dropped, and a low/high entered backwards is put the
      // right way round rather than printed as an error on the sheet.
      const cleanTemps = temps
        .map((t) => ({
          label: t.label.trim(),
          low: toNumber(t.low),
          high: toNumber(t.high),
          unit: TEMP_UNITS.includes(t.unit) ? t.unit : 'C',
        }))
        .filter((t) => t.label && t.low !== null)
        .map((t) => (t.high !== null && t.high < t.low ? { ...t, low: t.high, high: t.low } : t));

      const fields = {
        name: name.trim(),
        product_type_id: productTypeId || null,
        steps: parseSteps(steps),
        temperatures: cleanTemps,
      };
      if (editing) await updateMethod(method.id, fields);
      else await addMethod(fields);
      await onSaved();
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="asset-modal-backdrop" onClick={onClose}>
      <div
        className="version-modal"
        role="dialog"
        aria-modal="true"
        aria-label={editing ? `Edit ${method.name}` : 'New method'}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="asset-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <header className="version-modal-head">
          <div>
            <h3>{editing ? 'Edit method' : 'New method'}</h3>
            <span className="formula-detail-type">
              Steps and temperatures, shared by every formula you attach it to
            </span>
          </div>
        </header>

        <form onSubmit={handleSubmit}>
          <div className="lab-form-grid">
            <label className="lab-form-wide">
              Method name
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Emulsified body cream"
                required
              />
            </label>
            <label>
              Product type
              <select value={productTypeId} onChange={(e) => setProductTypeId(e.target.value)}>
                <option value="">Any</option>
                {(productTypes || []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <h4 className="batch-section-heading">Temperatures</h4>
          <p className="batch-form-hint">
            Leave the second box empty for a single temperature, or fill it for a range.
          </p>

          <datalist id="temp-labels">
            {LABEL_SUGGESTIONS.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>

          <div className="temp-rows">
            {temps.map((temp, i) => (
              <div className="temp-row" key={i}>
                <input
                  type="text"
                  list="temp-labels"
                  className="temp-label"
                  value={temp.label}
                  onChange={(e) => updateTemp(i, 'label', e.target.value)}
                  placeholder="Add fragrance at"
                />
                <input
                  type="number"
                  step="any"
                  className="temp-value"
                  value={temp.low}
                  onChange={(e) => updateTemp(i, 'low', e.target.value)}
                  placeholder="45"
                />
                <span className="temp-dash">–</span>
                <input
                  type="number"
                  step="any"
                  className="temp-value"
                  value={temp.high}
                  onChange={(e) => updateTemp(i, 'high', e.target.value)}
                  placeholder="50"
                />
                <select
                  className="temp-unit"
                  value={temp.unit}
                  onChange={(e) => updateTemp(i, 'unit', e.target.value)}
                  aria-label="Unit"
                >
                  {TEMP_UNITS.map((u) => (
                    <option key={u} value={u}>
                      °{u}
                    </option>
                  ))}
                </select>
                {temps.length > 1 && (
                  <button
                    type="button"
                    className="yield-remove"
                    onClick={() => setTemps((rows) => rows.filter((_, idx) => idx !== i))}
                    aria-label="Remove temperature"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            <button type="button" className="yield-add" onClick={() => setTemps((r) => [...r, blankTemp()])}>
              + Add temperature
            </button>
          </div>

          <h4 className="batch-section-heading">Steps</h4>
          <p className="batch-form-hint">One step per line. They're numbered for you.</p>
          <textarea
            className="method-textarea"
            rows={10}
            value={steps}
            onChange={(e) => setSteps(e.target.value)}
            placeholder={
              'Melt the oils and butters in a double boiler\n' +
              'Heat the water phase separately to the same temperature\n' +
              'Blend with a stick blender for 2 minutes\n' +
              'Cool before adding fragrance and preservative\n' +
              'Pour into sanitised jars and leave uncovered until set'
            }
          />

          {error && <p className="formula-error">{error}</p>}

          <div className="ingredient-form-actions">
            <button type="button" className="ghost-button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="primary-action" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save method' : 'Create method'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
